import { useEffect, useState } from "react";

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:5001";

export function assetUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * Fetches a content collection (why-items, services, partners) from the API.
 * Falls back to the caller-provided `fallback` array if the API is unreachable
 * so the public site never renders blank during dev/outage.
 */
export function useContent(resource, fallback = []) {
  const [items, setItems] = useState(fallback);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/content/${resource}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        setItems(Array.isArray(data) && data.length ? data : fallback);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        setItems(fallback);
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource]);

  return { items, loading, error };
}

/**
 * Fetches page-level settings by key prefix (e.g. "rules.", "halloffame.").
 * Falls back to caller-provided defaults for any missing keys.
 */
export function useSettings(prefix, fallback = {}) {
  const [settings, setSettings] = useState(fallback);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/settings?prefix=${encodeURIComponent(prefix)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (cancelled) return;
        const populated = Object.fromEntries(
          Object.entries(data).filter(([, v]) => v != null && v !== "")
        );
        setSettings({ ...fallback, ...populated });
      })
      .catch(() => !cancelled && setSettings(fallback));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefix]);
  return settings;
}

/**
 * Fetches the active event pages that are flagged to be featured publicly
 * (`show_in_nav`), ordered by the admin's sort order.
 *
 * These render as pills in the landing page hero, replacing the default
 * "Register Now" CTA whenever at least one event is live — several leagues /
 * events often run at once, so they get the hero rather than the header.
 */
export function useFeaturedEvents() {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/events`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (cancelled || !Array.isArray(data)) return;
        setEvents(data.filter((e) => e.show_in_nav));
      })
      .catch(() => !cancelled && setEvents([]));
    return () => { cancelled = true; };
  }, []);
  return events;
}
